/* Common Imports*/

import { styled, useMediaQuery } from "@mui/system";
import React from "react";

/* Redux Imports */

import { ObjectId } from "mongodb";
import { BusinessProfileInterface, PropertyInterface, UserInterface } from "../../../../../models";
import { StoreLeadsThunk } from "../../../../../redux-magic/store";

/* Component Imports */

import { LocalPhoneOutlined } from "@mui/icons-material";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import { IconButton, Link, Skeleton, Snackbar, Typography, useTheme } from "@mui/material";
import MuiAlert, { AlertProps } from "@mui/material/Alert";
import { useRouter } from "next/router";
import { createLoginFlow, LoginFlowTriggers } from "../../../../../lib/auth/helpers";
import BusinessProfileTypeString from "../../../../../lib/BusinessProfileTypeString";
import StateInterface from "../../../../../redux-magic/state-interface";
import BeegruButton from "../../../../common-components/buttons/BeegruButton";

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(props, ref) {
	return (
		<MuiAlert
			elevation={6}
			ref={ref}
			variant="filled"
			{...props}
		/>
	);
});

/* Styled Components */

const Container = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
	padding: "1rem",
	borderRadius: "16px",
	background: theme.palette.background.paper,
	margin: "0rem 0rem 0rem 0rem",
}));

const Heading = styled(Typography)(({ theme }) => ({
	margin: "0rem 0rem 0.75rem 0rem",
	width: "100%",
	fontWeight: "500",
	fontSize: "1rem",
	[theme.breakpoints.up("sm")]: {
		fontSize: "1rem",
	},
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1.125rem",
	},
}));

const ContactDiv = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	gap: "0.5rem",
	[theme.breakpoints.up("md1190")]: {
		margin: "0rem 0rem 1rem 0rem",
	},
	[theme.breakpoints.down("md1190")]: {
		flexDirection: "row",
		justifyContent: "flex-start",
		alignItems: "flex-start",
		gap: "0.75rem",
	},
}));

const ContactImg = styled("img")(({ theme }) => ({
	height: "4rem",
	width: "4rem",
	borderRadius: "8px",
	boxShadow: "0px 4px 18px 0px rgba(0, 0, 0, 0.08)",
	[theme.breakpoints.down("md1190")]: {
		height: "2.5rem",
		width: "2.5rem",
	},
}));

const NameTypeContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	[theme.breakpoints.down("md1190")]: {
		flexDirection: "row",
		justifyContent: "space-between",
		padding: "0rem 0rem 0rem 0rem",
		alignItems: "flex-start",
		gap: "0.5rem",
		width: "100%",
	},
}));

const ContactName = styled(Link)(({ theme }) => ({
	fontWeight: "400",
	fontSize: "0.875rem",

	[theme.breakpoints.up("md1190")]: {
		fontSize: "1rem",
	},
	color: theme.palette.mode == "dark" ? "#ffffff" : "#000000",
	textDecoration: "none",
	textAlign: "left",
	"&:hover": {
		color: theme.palette.primary.main,
	},
	// [theme.breakpoints.up("md")]: {
	// 	textAlign: "center",
	// },
}));

const ContactUserType = styled(Typography)(({ theme }) => ({
	fontWeight: "400",
	fontSize: "0.75rem",
	lineHeight: "1rem",
	color: theme.palette.mode == "dark" ? "#ffffff" : "#00000099",
	textAlign: "center",
}));

const ButtonContainer = styled("div")(({ theme }) => ({
	[theme.breakpoints.down("md1190")]: {
		display: "none",
	},
}));

const Snack = styled(Snackbar)(({ theme }) => ({
	display: "flex",
	justifyContent: "center",
	alignItems: "center",
	width: "auto",
	margin: "2.5rem 0rem 0rem 0rem",
	[theme.breakpoints.only("xxs")]: {
		width: "auto",
	},
	/* XS breakpoint */
	[theme.breakpoints.only("xs")]: {
		width: "auto",
	},
	/* XSPLUS breakpoint */
	[theme.breakpoints.only("xsPlus")]: {
		width: "auto",
	},
	/* SM breakpoint */
	[theme.breakpoints.only("sm")]: {
		width: "auto",
	},
	[theme.breakpoints.only("smPlus")]: {
		width: "85%",
	},
	/* MD breakpoint */
	[theme.breakpoints.only("md")]: {
		width: "auto",
	},
	/* LG breakpoint */
	[theme.breakpoints.up("lg")]: {
		width: "56%",
	},
	/* 1080p 125% */
	[theme.breakpoints.up(theme.breakpoints.values.lg + 150)]: {
		width: "auto",
	},
	[theme.breakpoints.only("lgPlus")]: {
		margin: "2rem 0rem 0rem 0rem",
		width: "60%",
	},
	".&MuiSnackbar-root": {
		top: "0px",
	},
}));

interface LocalPropertyInterface extends Omit<PropertyInterface, "created_by"> {
	created_by: {
		is_business_profile: boolean;
		business_profile_id: string | ObjectId | BusinessProfileInterface;
		user_id: string | ObjectId | UserInterface;
	};
}

const ContactDetailCard = ({
	session,
	property_created_by,
	property_slug,
	property_title,
	property_show_contact_details,
	transaction_type,
	dispatch,
	loading,
	showOnPhone,
	boost_information,
	property_id,
	profile_context,
	login_flow_global_state,
}: {
	property_created_by: LocalPropertyInterface["created_by"];
	property_slug: string;
	property_title: string;
	property_show_contact_details: boolean;
	transaction_type: string;
	dispatch: Function;
	session: any;
	loading: boolean;
	showOnPhone?: boolean;
	boost_information: PropertyInterface["boost_information"];
	property_id: ObjectId | string;
	profile_context: StateInterface["profile_context"];
	login_flow_global_state: StateInterface["login_flow_global_state"];
}) => {
	const theme = useTheme();

	const router = React.useRef(useRouter()).current;

	const isDesktop = useMediaQuery("(min-width:600px)");

	const [open, setOpen] = React.useState(false);

	const localLang = {
		heading: router.locale === "ar-AE" ? "مدرج بواسطة" : router.locale === "kn-IN" ? "ಲಿಸ್ಟ್ ಮಾಡಿದವರು" : "Listed by",
		snackValue:
			router.locale === "ar-AE"
				? "سنتواصل معك قريبًا!"
				: router.locale === "kn-IN"
					? "ನಾವು ಶೀಘ್ರದಲ್ಲಿ ನಿಮ್ಮನ್ನು ಸಂಪರ್ಕಿಸುತ್ತೇವೆ!"
					: "We'll get back to you soon!",
		getACallback:
			router.locale === "ar-AE"
				? "احصل على مكالمة رجعية"
				: router.locale === "kn-IN"
					? "ಕಾಲ್‌ಬ್ಯಾಕ್ ಪಡೆಯಿರಿ"
					: "Get a callback",
		callNow: router.locale === "ar-AE" ? "اتصل الآن" : router.locale === "kn-IN" ? "ಈಗ ಕರೆ ಮಾಡಿ" : "Call now",
		viewNumber:
			router.locale === "ar-AE" ? "عرض الرقم" : router.locale === "kn-IN" ? "ಫೋನ್ ಸಂಖ್ಯೆಯನ್ನು ವೀಕ್ಷಿಸಿ" : "View Number",
		mentionSnack:
			router.locale === "ar-AR"
				? "لا تنس أن تذكر Beegru عندما تتصل"
				: router.locale === "kn-IN"
					? "ನೀವು ಕಾಲ್ ಮಾಡಿದಾಗ Beegru ಅನ್ನು ಹೇಳಲು ಮರೆತಿರಬೇಡಿ"
					: "Mention Beegru during your call for smoother communication.",
		mentionSnackCallBack:
			router.locale === "ar-AR"
				? "لا تنس أن تذكر Beegru عندما تتلقى مكالمة"
				: router.locale === "kn-IN"
					? "ನೀವು ಕರೆ ಸ್ವೀಕರಿಸಿದಾಗ Beegru ಅನ್ನು ಹೇಳಲು ಮರೆತಿರಬೇಡಿ"
					: "Mention Beegru during your call for smoother communication.",
	};

	const [snackopen, setSnackOpen] = React.useState<boolean>(false);

	const [snackBarMessage, setSnackBarMessage] = React.useState<string>("");

	const [creatorImage, setCreatorImage] = React.useState(
		property_created_by
			? property_created_by.is_business_profile
				? property_created_by.business_profile_id
					? (property_created_by.business_profile_id as BusinessProfileInterface).display_picture_url
						? (property_created_by.business_profile_id as BusinessProfileInterface).display_picture_url.exists
							? (property_created_by.business_profile_id as BusinessProfileInterface).display_picture_url.file_id
							: "https://beegru.com/images/icons/Avatar.svg"
						: "https://beegru.com/images/icons/Avatar.svg"
					: "https://beegru.com/images/icons/Avatar.svg"
				: property_created_by.user_id
					? (property_created_by.user_id as UserInterface).profile_picture
						? (property_created_by.user_id as UserInterface).profile_picture.exists
							? (property_created_by.user_id as UserInterface).profile_picture.file_id
							: "https://beegru.com/images/icons/Avatar.svg"
						: "https://beegru.com/images/icons/Avatar.svg"
					: "https://beegru.com/images/icons/Avatar.svg"
			: "https://beegru.com/images/icons/Avatar.svg",
	);

	React.useEffect(() => {
		setCreatorImage(
			property_created_by
				? property_created_by.is_business_profile
					? property_created_by.business_profile_id
						? (property_created_by.business_profile_id as BusinessProfileInterface).display_picture_url
							? (property_created_by.business_profile_id as BusinessProfileInterface).display_picture_url.exists
								? (property_created_by.business_profile_id as BusinessProfileInterface).display_picture_url.file_id
								: "https://beegru.com/images/icons/Avatar.svg"
							: "https://beegru.com/images/icons/Avatar.svg"
						: "https://beegru.com/images/icons/Avatar.svg"
					: property_created_by.user_id
						? (property_created_by.user_id as UserInterface).profile_picture
							? (property_created_by.user_id as UserInterface).profile_picture.exists
								? (property_created_by.user_id as UserInterface).profile_picture.file_id
								: "https://beegru.com/images/icons/Avatar.svg"
							: "https://beegru.com/images/icons/Avatar.svg"
						: "https://beegru.com/images/icons/Avatar.svg"
				: "https://beegru.com/images/icons/Avatar.svg",
		);
	}, [property_created_by]);

	const creatorName: string = property_created_by
		? property_created_by.is_business_profile
			? property_created_by.business_profile_id !== ""
				? (property_created_by.business_profile_id as BusinessProfileInterface).title
				: property_created_by.user_id !== ""
					? (property_created_by.user_id as UserInterface).first_name +
						" " +
						(property_created_by.user_id as UserInterface).last_name
					: ""
			: property_created_by.user_id !== ""
				? (property_created_by.user_id as UserInterface).first_name +
					" " +
					(property_created_by.user_id as UserInterface).last_name
				: ""
		: "";

	const creatorType: string = property_created_by
		? property_created_by.is_business_profile
			? property_created_by.business_profile_id !== ""
				? (property_created_by.business_profile_id as BusinessProfileInterface).business_profile_type
				: "user"
			: "user"
		: "user";

	const creator_slug: ObjectId | string = property_created_by
		? property_created_by.is_business_profile
			? property_created_by.business_profile_id !== ""
				? (property_created_by.business_profile_id as BusinessProfileInterface).slug
				: property_created_by.user_id !== ""
					? (property_created_by.user_id as UserInterface)._id
					: ""
			: property_created_by.user_id !== ""
				? (property_created_by.user_id as UserInterface)._id
				: ""
		: "";

	const creatorPhone = property_created_by
		? property_created_by.is_business_profile
			? property_created_by.business_profile_id !== ""
				? (property_created_by.business_profile_id as BusinessProfileInterface).phone.phone_number ||
					(property_created_by.user_id as UserInterface).phone
				: property_created_by.user_id !== ""
					? (property_created_by.user_id as UserInterface).phone
					: ""
			: property_created_by.user_id !== ""
				? (property_created_by.user_id as UserInterface).phone
				: ""
		: "";

	const creatorCountryCode = property_created_by
		? property_created_by.is_business_profile
			? property_created_by.business_profile_id !== ""
				? (property_created_by.business_profile_id as BusinessProfileInterface).phone.country_code ||
					(property_created_by.user_id as UserInterface).country_code
				: property_created_by.user_id !== ""
					? (property_created_by.user_id as UserInterface).country_code
					: ""
			: property_created_by.user_id !== ""
				? (property_created_by.user_id as UserInterface).country_code
				: ""
		: "";

	if (loading) {
		return (
			<Container
				sx={{
					[theme.breakpoints.up("md1190")]: {
						display: showOnPhone ? "none" : "block",
					},
					[theme.breakpoints.down("md1190")]: {
						display: showOnPhone ? "block" : "none",
					},
				}}
			>
				<Heading>
					<Skeleton
						animation="wave"
						variant="rectangular"
						width="5rem"
						height="1rem"
						style={{ borderRadius: "0.5rem" }}
					/>
				</Heading>
				<ContactDiv>
					<Skeleton
						animation="wave"
						variant="rectangular"
						width="4.5rem"
						height="4.5rem"
						style={{ borderRadius: "8px" }}
					/>
					<ContactName>
						<Skeleton
							animation="wave"
							variant="rectangular"
							width="10rem"
							height="1rem"
							style={{ borderRadius: "0.5rem" }}
						/>
					</ContactName>
				</ContactDiv>
				<Skeleton
					animation="wave"
					variant="rectangular"
					width="100%"
					height="3rem"
				/>
			</Container>
		);
	}

	const submit = async (call_now: boolean) => {
		call_now ? null : setSnackOpen(true);

		const first_name = session ? (session.user ? (session.user.dbUser ? session.user.dbUser.first_name : "") : "") : "";
		const last_name = session ? (session.user ? (session.user.dbUser ? session.user.dbUser.last_name : "") : "") : "";
		const page_title = session
			? session.user
				? session.user.dbUser
					? session.user.dbUser.pages
						? session.user.dbUser.pages[0].page_title
						: ""
					: ""
				: ""
			: "";

		await dispatch(
			StoreLeadsThunk({
				name:
					page_title && page_title !== "" ? page_title : first_name && last_name ? first_name + " " + last_name : "",
				phone: {
					country_code: session
						? session.user
							? session.user.dbUser
								? session.user.dbUser.country_code
								: "+91"
							: "+91"
						: "+91",
					phone_number: session ? (session.user ? (session.user.dbUser ? session.user.dbUser.phone : "") : "") : "",
				},
				agreed_to_tc: true,
				source: "/properties/" + property_slug,
				listing_slug: property_slug,
				listing_id: property_id,
				listing_type: transaction_type == "ofb" ? "ofb property" : "property",
				is_guest: session ? false : true,
				listing_title: property_title,
				listing_owner: {
					is_business_profile: property_created_by.is_business_profile,
					user_id: property_created_by
						? property_created_by.is_business_profile
							? (property_created_by.user_id as string | ObjectId)
							: property_created_by.user_id !== ""
								? (property_created_by.user_id as UserInterface)._id
								: undefined
						: undefined,
					business_profile_id: property_created_by
						? property_created_by.is_business_profile
							? (property_created_by.business_profile_id as BusinessProfileInterface)._id
							: (property_created_by.business_profile_id as string | ObjectId)
						: undefined,
				},
				user_id: session ? (session.user ? (session.user.dbUser ? session.user.dbUser._id : "") : "") : "",
				is_lead_from_boost: boost_information ? boost_information.active : false,
				created_by: {
					is_business_profile: profile_context.is_business_profile,
					user_id: profile_context.user_id as string | ObjectId,
					business_profile_id: profile_context.business_profile_id as string | ObjectId,
				},
			}),
		);
	};

	const handleCloseSnack = () => {
		setSnackOpen(false);
	};

	return (
		<React.Fragment>
			<Snack
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
				open={snackopen}
				autoHideDuration={3000}
				onClose={handleCloseSnack}
			>
				<Alert
					onClose={handleCloseSnack}
					severity="success"
					sx={{ width: "100%", borderRadius: "0.75rem" }}
					variant="filled"
				>
					{snackBarMessage}
				</Alert>
			</Snack>

			{/* <Link
				href={(process.env.PRODUCTION_URL || " ") + router.locale + "/" + creatorType + "s" + "/" + creator_slug}
				rel="noopener"
				target="_self"
				sx={{
					textDecoration: "none",
					fontWeight: "500",
					fontSize: "1rem",
					color: "#000000",
					width: "100%",
				}}
			> */}
			{/* <LoginModal
				login_flow_global_state={login_flow_global_state}
				dispatch={dispatch}
				session={session}
			></LoginModal> */}
			<Container
				sx={{
					[theme.breakpoints.up("md1190")]: {
						display: showOnPhone ? "none" : "block",
					},
					[theme.breakpoints.down("md1190")]: {
						display: showOnPhone ? "block" : "none",
					},
				}}
			>
				<Heading>{localLang.heading}</Heading>

				<ContactDiv>
					<ContactName
						id="spp_contact_name"
						href={(process.env.PRODUCTION_URL || " ") + router.locale + "/" + creatorType + "s" + "/" + creator_slug}
						rel="noopener"
						target="_self"
						referrerPolicy="no-referrer"
					>
						{!(creatorImage as string).includes("/images/icons/Avatar.svg") ? (
							<ContactImg
								src={`/profile-picture/${creatorImage}`}
								referrerPolicy="no-referrer"
							/>
						) : (
							<ContactImg
								src="/images/icons/Avatar.svg"
								referrerPolicy="no-referrer"
							/>
						)}
					</ContactName>
					<NameTypeContainer>
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								justifyContent: "flex-start",
								alignItems: "flex-start",
							}}
						>
							<ContactName
								id="spp_contact_name"
								href={
									(process.env.PRODUCTION_URL || " ") + router.locale + "/" + creatorType + "s" + "/" + creator_slug
								}
								rel="noopener"
								target="_self"
								referrerPolicy="no-referrer"
							>
								{creatorName}
							</ContactName>

							<ContactUserType>{BusinessProfileTypeString(creatorType, router)}</ContactUserType>
						</div>
						<IconButton
							id="spp_call_now_button"
							href={"tel:" + creatorCountryCode + " " + creatorPhone}
							onClick={(event: any) => {
								session !== null ? submit(true) : null;
								if (isDesktop) {
									event.preventDefault();
									setOpen(!open);
								}
								setSnackOpen(true);
								setSnackBarMessage(localLang.mentionSnack);
							}}
							sx={{
								backgroundColor: "#DBF1E8",
								borderRadius: "0.5rem",
								// height: "3.5rem",
								// width: "3.5rem",
								[theme.breakpoints.up("md1190")]: {
									display: "none",
								},
							}}
						>
							<CallOutlinedIcon sx={{ color: "#12744F" }} />
						</IconButton>
					</NameTypeContainer>
				</ContactDiv>

				<ButtonContainer>
					{property_show_contact_details ? (
						<BeegruButton
							flavor="success"
							sx={{
								width: "100%",
								margin: "0rem 0rem 1rem 0rem",
							}}
							variant="contained"
							startIcon={<LocalPhoneOutlined />}
							href={"tel:" + creatorCountryCode + " " + creatorPhone}
							onClick={(event: any) => {
								session !== null ? submit(true) : null;
								if (isDesktop) {
									event.preventDefault();
									setOpen(!open);
								}
								setSnackOpen(true);
								setSnackBarMessage(localLang.mentionSnack);
							}}
						>
							{open ? creatorCountryCode + " " + creatorPhone : isDesktop ? "View Number" : localLang.callNow}
						</BeegruButton>
					) : null}
					<BeegruButton
						flavor="success"
						sx={{
							width: "100%",
							// margin: "0.5rem 0rem 0rem 0rem",
						}}
						variant={property_show_contact_details ? "outlined" : "contained"}
						onClick={(event: any) => {
							event.preventDefault();
							if ((login_flow_global_state?.ui.type || "redirect") === "modal") {
								if (session && session.authorized) {
									new Function(...(login_flow_global_state?.action.sign_in || ["session", "return"])).call(session);
									submit(false);
									setSnackOpen(true);
									setSnackBarMessage(localLang.mentionSnackCallBack);
								} else {
									const { startFlow, resetFlow }: LoginFlowTriggers = createLoginFlow({
										dispatch,
										trigger_source: "navbar-dropdown",
										flow_type: "modal",
										autofill: false,
										action: {
											callback_url:
												process.env.PRODUCTION_API_URL +
												"auth/end-session?sessionId=" +
												encodeURIComponent(session?.session_id) +
												"&callbackUrl=" +
												encodeURIComponent(router.asPath),
											sign_in: (session: any) => {},
											sign_up: (session: any) => {},
											sign_out: () => {},
										},
									});
									startFlow();
								}
							} else {
								setSnackOpen(true);
								setSnackBarMessage(localLang.mentionSnackCallBack);
								submit(false);
							}
							// session !== null ? submit(false) : signIn();
						}}
					>
						{localLang.getACallback}
					</BeegruButton>
				</ButtonContainer>
			</Container>
			{/* </Link> */}
		</React.Fragment>
	);
};

export default ContactDetailCard;
